// DOM操作ヘルパー
export function $(selector: string): HTMLElement | null {
  return document.querySelector(selector);
}

export function $$(selector: string): NodeListOf<HTMLElement> {
  return document.querySelectorAll(selector);
}

export function getApp(): HTMLElement {
  return document.getElementById('app')!;
}

export function render(html: string): void {
  getApp().innerHTML = html;
}

// イベントバインド
export function on(
  selector: string,
  event: string,
  handler: (e: Event, el: HTMLElement) => void
): void {
  document.querySelectorAll(selector).forEach((el) => {
    el.addEventListener(event, (e) => handler(e, el as HTMLElement));
  });
}

// カテゴリー色
export const CategoryColors: Record<string, string> = {
  warrior: '#e74c3c',
  thief: '#9b59b6',
  mage: '#3498db',
  machine: '#95a5a6',
  undead: '#1abc9c',
  dragon: '#e67e22',
  spirit: '#00bcd4',
  beast: '#8d6e63',
  angel: '#ffd700',
  demon: '#8e44ad',
  golem: '#607d8b',
  // 新カテゴリ
  castle: '#8b4513',
  city: '#4682b4',
  space: '#483d8b',
  movie: '#dc143c',
  ship: '#20b2aa',
  ghost: '#4b0082',
  park: '#ff69b4',
};

// カテゴリーアイコン
export const CategoryIcons: Record<string, string> = {
  warrior: '⚔️',
  thief: '🗡️',
  mage: '🔮',
  machine: '⚙️',
  undead: '💀',
  dragon: '🐉',
  spirit: '✨',
  beast: '🐺',
  angel: '👼',
  demon: '😈',
  golem: '🗿',
  // 新カテゴリ
  castle: '🏰',
  city: '🏙️',
  space: '🚀',
  movie: '🎬',
  ship: '🚢',
  ghost: '👻',
  park: '🎡',
};

// レアリティ色
export const RarityColors: Record<string, string> = {
  common: '#aaaaaa',
  rare: '#3498db',
  epic: '#9b59b6',
  legendary: '#ffd700',
};

// HPバー生成
export function hpBar(current: number, max: number, width: number = 100): string {
  const pct = Math.max(0, Math.min(100, (current / max) * 100));
  const color = pct > 50 ? '#2ecc71' : pct > 25 ? '#f39c12' : '#e74c3c';
  
  return `
    <div class="hp-bar" style="width: ${width}px; height: 8px; background: #333; border-radius: 4px; overflow: hidden;">
      <div style="width: ${pct}%; height: 100%; background: ${color}; transition: width 0.3s;"></div>
    </div>
  `;
}

// ボタン生成
export function button(text: string, id: string, variant: 'primary' | 'secondary' | 'ghost' = 'primary'): string {
  let style = '';
  let className = 'btn';
  
  if (variant === 'primary') {
    style = 'border-color: #00fff5; color: #00fff5;';
  } else if (variant === 'secondary') {
    style = 'border-color: #666; color: #999;';
  } else if (variant === 'ghost') {
    style = 'background: transparent; border: 1px solid rgba(255,255,255,0.3); color: #aaa;';
    className = 'btn btn-ghost';
  }
  
  return `<button class="${className}" id="${id}" style="${style}">${text}</button>`;
}

// カード表示
export function cardDisplay(
  name: string,
  power: number,
  category: string,
  rarity: string,
  abilities: string[] = [],
  onClick?: string
): string {
  const catColor = CategoryColors[category] ?? '#666';
  const catIcon = CategoryIcons[category] ?? '?';
  const rarityColor = RarityColors[rarity] ?? '#aaa';
  
  const clickAttr = onClick ? `onclick="${onClick}"` : '';
  const cursorStyle = onClick ? 'cursor: pointer;' : '';

  return `
    <div class="game-card" style="
      background: linear-gradient(135deg, rgba(30,30,50,0.9) 0%, rgba(20,20,40,0.95) 100%);
      border: 2px solid ${catColor};
      border-radius: 12px;
      padding: 10px;
      width: 100%;
      max-width: 140px;
      min-width: 90px;
      position: relative;
      ${cursorStyle}
    " ${clickAttr}>
      <div class="card-power" style="
        position: absolute;
        top: -8px;
        right: -8px;
        background: ${catColor};
        color: #fff;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 0.95rem;
        box-shadow: 0 0 10px ${catColor};
      ">${power}</div>
      
      <div style="font-size: 1.3rem; margin-bottom: 4px;">${catIcon}</div>
      <div style="font-size: 0.8rem; font-weight: bold; margin-bottom: 4px; color: #fff; word-break: break-all; line-height: 1.2;">${name}</div>
      <div style="font-size: 0.65rem; color: ${rarityColor}; text-transform: uppercase;">${rarity}</div>
      
      ${abilities.length > 0 ? `
        <div style="margin-top: 6px; font-size: 0.65rem; color: #aaa; border-top: 1px solid #333; padding-top: 4px; line-height: 1.3;">
          ${abilities.slice(0, 2).map((a) => `<div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">• ${a}</div>`).join('')}
          ${abilities.length > 2 ? `<div style="color: #666;">+${abilities.length - 2}...</div>` : ''}
        </div>
      ` : ''}
    </div>
  `;
}

// ステータスバー
export function statusBar(fans: number, round: number, phase: string): string {
  return `
    <div class="status-bar" style="
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      max-width: 900px;
      padding: 12px 24px;
      background: rgba(0,0,0,0.4);
      border-radius: 8px;
      margin-bottom: 20px;
      border: 1px solid rgba(0,255,245,0.2);
    ">
      <span style="color: #ffd700; font-weight: bold;">★ ${fans} Fans</span>
      <span style="color: #00fff5;">Round ${round}</span>
      <span style="color: #aaa;">${phase}</span>
    </div>
  `;
}

// プレイヤーカード
export function playerCard(
  name: string,
  fans: number,
  isBot: boolean,
  isMe: boolean = false
): string {
  const border = isMe ? '2px solid #00fff5' : '1px solid #444';
  const glow = isMe ? 'box-shadow: 0 0 15px rgba(0,255,245,0.3);' : '';
  
  return `
    <div style="
      background: rgba(40,40,60,0.8);
      ${border};
      border-radius: 8px;
      padding: 12px 16px;
      min-width: 150px;
      ${glow}
    ">
      <div style="font-size: 1.2rem; margin-bottom: 4px;">${isBot ? '🤖' : '👤'}</div>
      <div style="font-weight: bold; color: ${isMe ? '#00fff5' : '#fff'};">${name}</div>
      <div style="color: #ffd700; font-size: 0.9rem;">★ ${fans}</div>
    </div>
  `;
}

// 会場カード
export function venueCard(
  id: string,
  name: string,
  description: string,
  icon: string,
  isSelected: boolean = false
): string {
  const border = isSelected ? '2px solid #00fff5' : '1px solid #444';
  const glow = isSelected ? 'box-shadow: 0 0 20px rgba(0,255,245,0.4);' : '';
  
  return `
    <div class="venue-card" data-venue="${id}" style="
      background: rgba(40,40,60,0.8);
      ${border};
      border-radius: 12px;
      padding: 20px;
      min-width: 180px;
      cursor: pointer;
      transition: all 0.3s ease;
      ${glow}
    ">
      <div style="font-size: 2rem; margin-bottom: 8px;">${icon}</div>
      <div style="font-weight: bold; font-size: 1.1rem; margin-bottom: 4px;">${name}</div>
      <div style="font-size: 0.85rem; color: #aaa;">${description}</div>
    </div>
  `;
}

// バトルログエントリー
export function logEntry(message: string, type: 'normal' | 'attack' | 'ability' | 'result' = 'normal'): string {
  const colors: Record<string, string> = {
    normal: '#ccc',
    attack: '#ff6b6b',
    ability: '#9b59b6',
    result: '#ffd700',
  };
  
  return `<div style="color: ${colors[type]}; padding: 4px 0; border-bottom: 1px solid #333;">${message}</div>`;
}

// モーダル
export function modal(title: string, content: string, buttons: { text: string; id: string; primary?: boolean }[]): string {
  return `
    <div class="modal-overlay" style="
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0,0,0,0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    ">
      <div style="
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
        border: 2px solid #00fff5;
        border-radius: 16px;
        padding: 24px;
        max-width: 500px;
        width: 90%;
        box-shadow: 0 0 40px rgba(0,255,245,0.3);
      ">
        <h2 style="color: #00fff5; margin-bottom: 16px;">${title}</h2>
        <div style="margin-bottom: 20px;">${content}</div>
        <div style="display: flex; gap: 12px; justify-content: flex-end;">
          ${buttons.map((b) => `
            <button class="btn" id="${b.id}" style="
              ${b.primary ? 'background: #00fff5; color: #1a1a2e;' : ''}
            ">${b.text}</button>
          `).join('')}
        </div>
      </div>
    </div>
  `;
}

// アニメーション用クラス追加
export function addAnimation(selector: string, animation: string): void {
  const el = $(selector);
  if (el) {
    el.style.animation = animation;
  }
}

// 遅延実行
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
